import { getCostsAnalysis, getProfitabilityAnalysis } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Módulo del drawer "Análisis de Categorías" del tab Rentabilidad: la
// rentabilidad de UN mes abierta por ítem de una categoría (tiendas/marcas).
// Espejo de `salesAnalysis`: el body del drawer lee de acá y las cards son
// presentacionales. La sección "Cuentas & Contrapartes <ítem>" NO vive en este
// payload — la sirve `/costs/analysis` con el filtro del ítem elegido.

const state = () => ({
	/** YYYY-MM del mes analizado. Null con el drawer cerrado. */
	month: null,
	/** Categoría principal (la del chip o la del select del header). */
	category: null,
	/**
	 * Selección VISUAL de los selects del header: { tiendas: ["T1","T2"] }.
	 * La categoría principal arranca con todos sus ítems tildados (así su
	 * select se ve activo y las demás no). Lo que viaja al backend es sólo la
	 * selección PARCIAL — ver `effectiveFilters`.
	 */
	selectedItems: {},
	/** Payload del backend, congelado (sin observer profundo de Vue 2). */
	data: null,
	isLoading: false,
	error: null,

	// --- Sección "Cuentas & Contrapartes" (lazy) ---
	/**
	 * Payload de `/costs/analysis` del mes: `statement` (cascada de costos con
	 * sus cuentas) + `counterparts_ranking`. Se pide al endpoint que ya existe
	 * en vez de duplicar la cascada en el de rentabilidad, y respeta el mismo
	 * filtro de ítems que el análisis.
	 */
	detailData: null,
	isDetailLoading: false,
	detailError: null,
	// Buscador del ranking de contrapartes.
	counterpartsQuery: "",
	/**
	 * Ítem elegido en la lista de la sección (una tienda, una marca): acota
	 * el detalle —cascada de cuentas y contrapartes— a ese ítem. Null =
	 * todos los de la categoría, con el filtro del header.
	 */
	detailItem: null,
	/**
	 * Vista de la card izquierda: "items" (la lista de la categoría) o
	 * "accounts" (la cascada contable). Vive en el store porque decide QUÉ
	 * datos hacen falta: con la lista a la vista, elegir un ítem sólo
	 * refresca el ranking.
	 */
	detailMode: "items",
	/**
	 * Ítem al que corresponde la cascada cargada. Cuando no coincide con
	 * `detailItem`, la cascada está vieja y hay que pedirla — pero recién al
	 * pasar a la vista por cuenta, que es cuando se mira.
	 */
	statementItem: null,
	/**
	 * Cuenta elegida en la card de costos de la sección, que recorta el
	 * ranking de contrapartes (igual que el drawer de Costos). Null = todas.
	 */
	detailAccount: null,
	/** Nivel de `detailAccount`: "2" (grupo level_2) o "3" (cuenta level_3). */
	detailAccountLevel: null,
	/** Nivel 2 de `detailAccount` cuando es una cuenta: desambigua homónimas. */
	detailAccountGroup: null,
});

const mutations = {
	SET_CONTEXT(state, { month, category, selectedItems }) {
		state.month = month ?? null;
		state.category = category ?? null;
		state.selectedItems = Object.freeze(selectedItems ?? {});
	},
	SET_DATA(state, payload) { state.data = payload; },
	SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
	SET_ERROR(state, error) { state.error = error; },
	SET_DETAIL_DATA(state, payload) { state.detailData = payload; },
	SET_DETAIL_ITEM(state, item) { state.detailItem = item ?? null; },
	SET_DETAIL_MODE(state, mode) { state.detailMode = mode; },
	SET_STATEMENT_ITEM(state, item) { state.statementItem = item ?? null; },
	SET_DETAIL_ACCOUNT(state, { account, level, group }) {
		state.detailAccount = account ?? null;
		state.detailAccountLevel = level ?? null;
		state.detailAccountGroup = group ?? null;
	},
	// Reemplaza SÓLO el ranking dentro del detalle: statement y headline
	// conservan sus refs, así la cascada no se repinta ni colapsa sus grupos.
	SET_DETAIL_RANKING(state, ranking) {
		if (!state.detailData) return;
		state.detailData = Object.freeze({
			...state.detailData, counterparts_ranking: ranking ?? [],
		});
	},
	SET_DETAIL_LOADING(state, isLoading) { state.isDetailLoading = isLoading; },
	SET_DETAIL_ERROR(state, error) { state.detailError = error; },
	SET_COUNTERPARTS_QUERY(state, query) { state.counterpartsQuery = query; },
	RESET(state) {
		state.month = null;
		state.category = null;
		state.selectedItems = {};
		state.data = null;
		state.error = null;
		state.detailData = null;
		state.detailError = null;
		state.counterpartsQuery = "";
		state.detailItem = null;
		state.detailMode = "items";
		state.statementItem = null;
		state.detailAccount = null;
		state.detailAccountLevel = null;
		state.detailAccountGroup = null;
	},
};

/**
 * Filtro que viaja al backend. Una categoría con TODOS sus ítems tildados no
 * filtra: hacerlo dejaría fuera los documentos sin clasificar en ella ("Sin
 * tienda") y el total del drawer ya no cuadraría con la tabla del tab. Sólo
 * viaja la selección parcial.
 */
export function effectiveFilters(selectedItems, itemsByCategory = {}) {
	const filters = {};
	Object.entries(selectedItems || {}).forEach(([category, values]) => {
		const all = itemsByCategory[category] || [];
		if (values && values.length && values.length < all.length) {
			filters[category] = values;
		}
	});
	return Object.keys(filters).length ? filters : null;
}

/**
 * Filtro de atributos del detalle: el del header más, si el analista eligió
 * un ítem en la lista de la sección, ESE ítem — que manda sobre la selección
 * más amplia del header porque es la más específica.
 */
function detailFilters(state, rootState) {
	const filters = effectiveFilters(
		state.selectedItems, rootState.classification.itemsByCategory,
	);
	if (!state.detailItem || !state.category) return filters;
	return { ...(filters || {}), [state.category]: [state.detailItem] };
}

const actions = {
	/**
	 * Detalle del mes: cuentas de costo y ranking de contrapartes. Reusa
	 * `/costs/analysis` — su `statement` + `counterparts_ranking` son
	 * exactamente las dos cards de la sección, así que no hay endpoint nuevo.
	 * Respeta el mismo filtro de ítems que el análisis (el del header).
	 */
	async fetchItemDetail({ commit, state, rootState }, { keepSelection = false } = {}) {
		const month = state.month;
		if (!month) {
			commit("SET_DETAIL_DATA", null);
			return;
		}
		commit("SET_DETAIL_LOADING", true);
		commit("SET_DETAIL_ERROR", null);
		// El detalle arranca completo: todos los ítems de la categoría y
		// todas sus cuentas (`selectDetailItem` / `selectDetailRankingAccount`
		// lo acotan después).
		if (!keepSelection) {
			commit("SET_DETAIL_ITEM", null);
			commit("SET_DETAIL_ACCOUNT", { account: null, level: null, group: null });
		}
		try {
			const filters = detailFilters(state, rootState);
			const response = await getCostsAnalysis(month, null, null, filters);
			commit("SET_DETAIL_DATA", Object.freeze(response.data));
			commit("SET_STATEMENT_ITEM", state.detailItem);
		} catch (error) {
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
			commit(
				"SET_DETAIL_ERROR",
				extractErrorMessage(error, "Error al cargar el detalle de la categoría."),
			);
		} finally {
			commit("SET_DETAIL_LOADING", false);
		}
	},

	/**
	 * Recorta el ranking de contrapartes a la cuenta clickeada en la cascada de
	 * costos, sin recargar la sección: mismo mes y filtros, y del payload sólo
	 * se reemplaza `counterparts_ranking` (sin flash de "Cargando…"). Descarta
	 * respuestas obsoletas — manda la última cuenta elegida. Mismo contrato que
	 * `selectRankingAccount` del drawer de Costos. account=null = todas.
	 */
	/**
	 * Ítem elegido en la lista de la sección. Con la LISTA a la vista sólo se
	 * refresca el ranking (`only=ranking`): la cascada no está en pantalla y
	 * pedirla costaba cuatro lecturas de más. Queda marcada como vieja y se
	 * pide recién al pasar a la vista por cuenta. Con la CASCADA a la vista
	 * sí se pide todo, porque todo cambia. La cuenta elegida se descarta en
	 * los dos casos: era del universo anterior. `item` null vuelve a todos
	 * los ítems de la categoría.
	 */
	async selectDetailItem({ commit, dispatch, state }, item = null) {
		commit("SET_DETAIL_ITEM", item);
		commit("SET_DETAIL_ACCOUNT", { account: null, level: null, group: null });
		if (state.detailMode === "accounts") {
			await dispatch("fetchItemDetail", { keepSelection: true });
			return;
		}
		await dispatch("refreshDetailRanking");
	},

	/**
	 * Cambio de vista de la card izquierda. La cascada se pide acá, y sólo si
	 * la que hay es de otro ítem: el analista paga el recálculo cuando decide
	 * mirarla, no cada vez que cambia de tienda.
	 */
	async setDetailMode({ commit, dispatch, state }, mode) {
		commit("SET_DETAIL_MODE", mode);
		if (mode !== "accounts" || !state.detailData) return;
		if (state.statementItem === state.detailItem) return;
		await dispatch("fetchItemDetail", { keepSelection: true });
	},

	/**
	 * Refresca SÓLO el ranking con el filtro vigente (ítem + header), sin
	 * tocar la cascada. Descarta respuestas obsoletas: manda el último ítem.
	 */
	async refreshDetailRanking({ commit, state, rootState }) {
		if (!state.month) return;
		const item = state.detailItem;
		commit("SET_DETAIL_LOADING", true);
		try {
			const filters = detailFilters(state, rootState);
			const response = await getCostsAnalysis(
				state.month, null, null, filters, null, null, "ranking",
			);
			if (state.detailItem !== item) return;
			commit("SET_DETAIL_RANKING", response.data.counterparts_ranking);
		} catch (error) {
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
			commit(
				"SET_DETAIL_ERROR",
				extractErrorMessage(error, "Error al cargar el detalle de la categoría."),
			);
		} finally {
			commit("SET_DETAIL_LOADING", false);
		}
	},

	async selectDetailRankingAccount(
		{ commit, state, rootState }, { account = null, level = null, group = null } = {}
	) {
		commit("SET_DETAIL_ACCOUNT", { account, level, group });
		if (!state.month) return;
		try {
			const filters = detailFilters(state, rootState);
			const response = await getCostsAnalysis(
				state.month, account, null, filters, level, group, "ranking",
			);
			if (state.detailAccount !== account
				|| state.detailAccountLevel !== level
				|| state.detailAccountGroup !== group) return;
			commit("SET_DETAIL_RANKING", response.data.counterparts_ranking);
		} catch (error) {
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
		}
	},

	async fetchProfitabilityAnalysis(
		{ commit, rootState }, { month, category = null, selectedItems = null } = {}
	) {
		commit("SET_CONTEXT", { month, category, selectedItems });
		commit("SET_LOADING", true);
		commit("SET_ERROR", null);
		try {
			const filters = effectiveFilters(
				selectedItems, rootState.classification.itemsByCategory
			);
			const response = await getProfitabilityAnalysis(month, category, filters);
			commit("SET_DATA", Object.freeze(response.data));
			// El detalle abierto es de otro mes/filtro: se descarta (la
			// sección vuelve a pedirlo al desplegarse) junto con el recorte
			// por cuenta, que pertenecía a esa cascada.
			commit("SET_DETAIL_DATA", null);
			commit("SET_DETAIL_ITEM", null);
			commit("SET_STATEMENT_ITEM", null);
			commit("SET_DETAIL_ACCOUNT", { account: null, level: null, group: null });
		} catch (error) {
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
			commit(
				"SET_ERROR",
				extractErrorMessage(error, "Error al cargar el análisis de categorías.")
			);
		} finally {
			commit("SET_LOADING", false);
		}
	},
};

const getters = {
	// Ranking filtrado por el buscador de la card (mismo criterio que el
	// drawer de Costos: match por nombre, sin distinguir mayúsculas).
	filteredCounterparts: (state) => {
		const ranking = (state.detailData && state.detailData.counterparts_ranking) || [];
		const query = state.counterpartsQuery.trim().toLowerCase();
		if (!query) return ranking;
		return ranking.filter((item) => item.name.toLowerCase().includes(query));
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
